/**
 * Hourly comparison — "how is today going, hour by hour?"
 *
 * One IST day's hours against the previous day and/or the average of the N
 * days before it. Lets the marketing team catch an ad-delivery drop, a broken
 * page or a payment issue the same day instead of in tomorrow's day report.
 *
 * Reads /checkout-analytics-hourly, which returns sparse
 * { day, hour, region, version, ...metrics } rows for the whole window plus
 * `asOf` (server time). Rows are summed here for the pages in view, the same
 * way daysForSlugs narrows the daily payload, so a metric switch never
 * refetches. "Today" and the cut-off hour come from `asOf`, not the viewer's
 * clock.
 *
 * Hand-drawn SVG like CampaignTrend: no chart library on this route.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import moment from "moment";
import { Box, CircularProgress, Tooltip } from "@material-ui/core";
import BarChartIcon from "@material-ui/icons/BarChart";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { URL } from "../../config/config";
import { fmt, pctChange } from "./analyticsUtils";
import { Delta } from "./AnalyticsViews";

const G = "#04b85c";
const INK = "#0e1f18";
const BODY = "#33443c";
const MUTED = "#7b8a83";
const LINE = "#e6ebe8";
const UP = "#0f7a3d";
const DOWN = "#c0392b";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

// Fixed order: the base day is always green, previous day blue, average amber.
const COLOR = { base: G, prev: "#4a6fe3", avg: "#e08a1e" };

export const HOURLY_METRICS = [
  { key: "leads", label: "Leads" },
  { key: "purchases", label: "Paid" },
  { key: "abandoned", label: "Abandoned" },
  { key: "callbacks", label: "Callbacks" },
  { key: "revenue", label: "Revenue", money: true },
];
const N_PRESETS = [3, 5, 7, 14, 30];
const MAX_N = 90;

const shiftDay = (key, n) =>
  moment(key, "YYYY-MM-DD").add(n, "day").format("YYYY-MM-DD");
const hourLabel = (h) =>
  `${h % 12 === 0 ? 12 : h % 12} ${h < 12 || h === 24 ? "AM" : "PM"}`;
const hourRange = (h) => `${hourLabel(h)} – ${hourLabel((h + 1) % 24)}`;
const dayLabel = (key) => moment(key, "YYYY-MM-DD").format("ddd DD MMM");
// Delta prints values as given: averages go in at one decimal and money in
// whole rupees, or the badge reads "−13.143".
const tidy = (v, { avg, money } = {}) =>
  v == null ? v : money ? Math.round(v) : avg ? Math.round(v * 10) / 10 : v;
const sumTo = (vals, cut) =>
  vals.slice(0, cut).reduce((a, v) => a + (v || 0), 0);

// Delta draws nothing for 0 vs 0, which leaves "Today is" hanging on a
// quiet page. Say so instead.
const Change = ({ current, previous }) =>
  current === 0 && previous === 0 ? (
    <span style={{ fontSize: 11.5, fontWeight: 700, color: "#aab6b0" }}>
      no change
    </span>
  ) : (
    <Delta current={current} previous={previous} />
  );

/** IST { day, hour, minute } for a server timestamp. */
const istParts = (iso) => {
  const d = new Date(new Date(iso).getTime() + IST_OFFSET_MS);
  return {
    day: d.toISOString().slice(0, 10),
    hour: d.getUTCHours(),
    minute: d.getUTCMinutes(),
  };
};

const label = {
  fontSize: 11,
  fontWeight: 700,
  color: MUTED,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  whiteSpace: "nowrap",
};

const HourlyComparison = ({
  slugs, // [{ region, version }] to narrow to; null = every page in the filters
  channel,
  source, // comma-joined raw sources, as the pages endpoint takes them
  utmCampaign,
  refreshKey,
  classes, // the page's makeStyles classes: chips reuse rangeChip / dateInput
}) => {
  const [metric, setMetric] = useState("leads");
  // null = "today" as the SERVER sees it; only a picked day is pinned. The
  // request then omits `date`, so a viewer clock that is off, or a page left
  // open across midnight, can't ask for the wrong day.
  const [pickedDay, setPickedDay] = useState(null);
  const [usePrev, setUsePrev] = useState(true);
  const [useAvg, setUseAvg] = useState(true);
  const [n, setN] = useState(7);
  const [nText, setNText] = useState("");
  const [view, setView] = useState("chart");
  const [till, setTill] = useState(24);
  // Starts closed everywhere: the header line already answers "how is today
  // going", and the full chart only appears when asked for.
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ rows: [], asOf: null, failed: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Ignore a response that lands after a newer request (fast day switches).
    let live = true;
    setLoading(true);
    // Always N days back, whether or not the average is ticked, so toggling
    // it never refetches.
    const params = new URLSearchParams({ days: String(n), channel });
    if (pickedDay) params.set("date", pickedDay);
    if (source) params.set("source", source);
    if (utmCampaign) params.set("utmCampaign", utmCampaign);
    axios
      .get(`${URL}/superadmin/checkout-analytics-hourly?${params}`)
      .then((r) => {
        if (live)
          setData({
            rows: r.data.rows || [],
            asOf: r.data.asOf,
            failed: false,
          });
      })
      // A failure must not render as real zeros: on a monitoring view that
      // reads as "sales dropped to nothing".
      .catch(() => live && setData({ rows: [], asOf: null, failed: true }))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [pickedDay, n, channel, source, utmCampaign, refreshKey]);

  const now = useMemo(
    () => istParts(data.asOf || new Date().toISOString()),
    [data.asOf],
  );
  const baseDay = pickedDay || now.day;
  const isToday = baseDay === now.day;
  // Hours [0, cut) are compared on every day. Today stops at the last
  // completed hour; a past day can be cut anywhere ("Tuesday till 3 PM").
  const cut = isToday ? now.hour : till;
  const pickDay = (k) => {
    setPickedDay(k === now.day ? null : k);
    setTill(24);
  };

  // day -> 24 hourly values of the chosen metric, for the pages in view.
  const byDay = useMemo(() => {
    const wanted = slugs
      ? new Set(slugs.map((s) => `${s.region}|${s.version}`))
      : null;
    const map = new Map();
    for (const r of data.rows) {
      if (wanted && !wanted.has(`${r.region}|${r.version}`)) continue;
      if (!map.has(r.day)) map.set(r.day, Array(24).fill(0));
      map.get(r.day)[r.hour] += r[metric] || 0;
    }
    return map;
  }, [data.rows, slugs, metric]);

  const series = useMemo(() => {
    const day = (k) => byDay.get(k) || Array(24).fill(0);
    const base = day(baseDay).map((v, h) =>
      isToday && h >= now.hour ? null : v,
    );
    const yesterday = shiftDay(now.day, -1);
    const out = [
      {
        id: "base",
        label: isToday
          ? "Today"
          : baseDay === yesterday
            ? "Yesterday"
            : dayLabel(baseDay),
        vals: base,
      },
    ];
    if (usePrev) {
      const p = shiftDay(baseDay, -1);
      out.push({
        id: "prev",
        label: isToday ? "Yesterday" : `Previous day (${dayLabel(p)})`,
        short: isToday ? "Yesterday" : "Previous day",
        vals: day(p),
      });
    }
    if (useAvg) {
      const acc = Array(24).fill(0);
      for (let i = 1; i <= n; i++)
        day(shiftDay(baseDay, -i)).forEach((v, h) => (acc[h] += v));
      out.push({
        id: "avg",
        label: `Avg of last ${n} days`,
        short: `${n}-day avg`,
        avg: true,
        vals: acc.map((v) => v / n),
      });
    }
    return out;
  }, [byDay, baseDay, isToday, now, usePrev, useAvg, n]);

  const m = HOURLY_METRICS.find((x) => x.key === metric);
  const show = (v, avg) =>
    v == null
      ? "—"
      : m.money
        ? `₹${fmt(Math.round(v))}`
        : avg
          ? Number(v.toFixed(1)).toLocaleString("en-IN")
          : fmt(Math.round(v));
  const tillText = cut === 24 ? "full day" : `till ${hourLabel(cut)}`;
  const asOfText = data.asOf
    ? moment(new Date(data.asOf).getTime() + IST_OFFSET_MS)
        .utc()
        .format("h:mm A")
    : "";
  const baseTotal = sumTo(series[0].vals, cut);

  const heading = (
    <Box
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: 15,
        fontWeight: 700,
        color: INK,
      }}
    >
      <BarChartIcon style={{ color: G, fontSize: 19 }} />
      Hourly comparison
    </Box>
  );

  // Accordion header: one line that answers "how is today going" on its
  // own, and opens the full section on click.
  const ref = series.find((s) => s.id === "avg") || series[1];
  const accordionHead = (
    <Box
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onClick={() => setOpen((o) => !o)}
      onKeyDown={(e) =>
        (e.key === "Enter" || e.key === " ") && setOpen((o) => !o)
      }
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        flexWrap: "wrap",
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      {heading}
      {loading ? (
        <CircularProgress size={14} style={{ color: G }} />
      ) : data.failed ? (
        <span style={{ fontSize: 12.5, color: DOWN }}>
          Couldn't load hourly numbers
        </span>
      ) : (
        <Box
          style={{
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
            fontSize: 12.5,
            color: BODY,
          }}
        >
          <span>
            {series[0].label} · {m.label} {tillText}:{" "}
            <b style={{ color: INK, fontSize: 14 }}>{show(baseTotal)}</b>
          </span>
          {ref && (
            <span>
              vs {ref.short}:{" "}
              <Change
                current={tidy(baseTotal, m)}
                previous={tidy(sumTo(ref.vals, cut), { ...m, avg: ref.avg })}
              />
            </span>
          )}
        </Box>
      )}
      <Box
        style={{
          marginLeft: "auto",
          display: "flex",
          alignItems: "center",
          gap: 2,
          color: UP,
          fontSize: 12.5,
          fontWeight: 700,
        }}
      >
        {open ? "Hide" : "Show hourly view"}
        {open ? (
          <ExpandLessIcon style={{ fontSize: 20 }} />
        ) : (
          <ExpandMoreIcon style={{ fontSize: 20 }} />
        )}
      </Box>
    </Box>
  );

  if (!open) return accordionHead;

  return (
    <Box>
      {accordionHead}
      <Box style={{ height: 1, background: LINE, margin: "14px -18px 14px" }} />
      <Box
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Box style={{ color: MUTED, fontSize: 13, marginTop: 2 }}>
            {m.label} hour by hour (IST), against the days you pick. Follows the
            filters above.
          </Box>
        </Box>
        <Box style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <Box
            style={{
              display: "flex",
              border: `1px solid ${LINE}`,
              borderRadius: 10,
              overflow: "hidden",
              background: "#f4f7f5",
            }}
          >
            {[
              ["chart", "Per hour"],
              ["table", "Table"],
            ].map(([k, l]) => (
              <button
                key={k}
                type="button"
                onClick={() => setView(k)}
                style={{
                  border: 0,
                  cursor: "pointer",
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: "inherit",
                  background: view === k ? "#fff" : "none",
                  color: view === k ? INK : MUTED,
                  boxShadow: view === k ? "0 1px 2px rgba(0,0,0,.08)" : "none",
                }}
              >
                {l}
              </button>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        style={{
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          gap: "10px 14px",
          marginTop: 14,
          alignItems: "center",
        }}
      >
        <span style={label}>Metric</span>
        <Box style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {HOURLY_METRICS.map((x) => (
            <button
              key={x.key}
              type="button"
              className={`${classes.rangeChip} ${metric === x.key ? classes.rangeChipOn : ""}`}
              onClick={() => setMetric(x.key)}
            >
              {x.label}
            </button>
          ))}
        </Box>
        <span style={label}>Day</span>
        <Box
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {[
            [now.day, "Today"],
            [shiftDay(now.day, -1), "Yesterday"],
          ].map(([k, l]) => (
            <button
              key={l}
              type="button"
              className={`${classes.rangeChip} ${baseDay === k ? classes.rangeChipOn : ""}`}
              onClick={() => pickDay(k)}
            >
              {l}
            </button>
          ))}
          {/* Native date input, as in the range bar: speaks YYYY-MM-DD, no
              timezone shift. Read the value before setState (React 16 pools
              synthetic events). */}
          <input
            type="date"
            className={classes.dateInput}
            value={baseDay}
            max={now.day}
            onChange={(e) => {
              const v = e.target.value;
              if (v && v <= now.day) pickDay(v);
            }}
          />
        </Box>
        <span style={label}>Compare with</span>
        <Box
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {[
            [
              "prev",
              isToday ? "Yesterday" : "Previous day",
              usePrev,
              setUsePrev,
              useAvg,
            ],
            ["avg", "Average of last", useAvg, setUseAvg, usePrev],
          ].map(([id, l, on, set, other]) => (
            <span
              key={id}
              role="checkbox"
              aria-checked={on}
              tabIndex={0}
              // At least one comparison stays on — a chart of one day alone
              // answers nothing this section exists for.
              onClick={() => (on && !other ? null : set(!on))}
              onKeyDown={(e) => {
                if (e.key !== " " && e.key !== "Enter") return;
                e.preventDefault();
                if (!(on && !other)) set(!on);
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                border: `1px solid ${LINE}`,
                borderRadius: 999,
                padding: "4px 10px 4px 6px",
                background: "#fff",
                cursor: "pointer",
                fontSize: 12.5,
                fontWeight: 600,
                color: on ? BODY : MUTED,
                userSelect: "none",
              }}
            >
              <span
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: 5,
                  display: "grid",
                  placeItems: "center",
                  color: "#fff",
                  fontSize: 11,
                  background: on ? COLOR[id] : "transparent",
                  border: on ? "none" : "1.5px solid #c3cfc9",
                }}
              >
                {on ? "✓" : ""}
              </span>
              {l}
            </span>
          ))}
          {N_PRESETS.map((k) => (
            <button
              key={k}
              type="button"
              className={`${classes.rangeChip} ${useAvg && n === k ? classes.rangeChipOn : ""}`}
              style={{ padding: "3px 10px", fontSize: 12 }}
              onClick={() => {
                setN(k);
                setNText("");
                setUseAvg(true);
              }}
            >
              {k}
            </button>
          ))}
          <input
            type="number"
            min={2}
            max={MAX_N}
            placeholder="N"
            title="Custom number of days"
            className={classes.dateInput}
            style={{ width: 62, padding: "3px 8px" }}
            value={nText}
            onChange={(e) => setNText(e.target.value)}
            onBlur={() => {
              const v = parseInt(nText, 10);
              if (!v) return;
              const clamped = Math.max(2, Math.min(MAX_N, v));
              setN(clamped);
              setNText(N_PRESETS.includes(clamped) ? "" : String(clamped));
              setUseAvg(true);
            }}
            onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
          />
          <span style={{ color: MUTED, fontSize: 13 }}>days</span>
        </Box>
      </Box>

      <Box style={{ height: 1, background: LINE, margin: "16px -18px" }} />

      <Box
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          marginBottom: 10,
        }}
      >
        <Box style={{ fontSize: 13, fontWeight: 700, color: INK }}>
          {isToday ? "Till this hour" : "Totals"}{" "}
          <span style={{ color: MUTED, fontWeight: 500 }}>
            ·{" "}
            {cut === 24
              ? "whole day"
              : cut === 0
                ? "the first hour of today hasn't finished yet"
                : `12 AM – ${hourLabel(cut)}, the same hours on every day`}
          </span>
        </Box>
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontFamily: MONO,
            fontSize: 11,
            color: MUTED,
          }}
        >
          {isToday ? (
            asOfText && <span>as of {asOfText}</span>
          ) : (
            <select
              className={classes.dateInput}
              value={till}
              onChange={(e) => setTill(Number(e.target.value))}
            >
              <option value={24}>Full day</option>
              {Array.from({ length: 23 }, (_, i) => i + 1).map((h) => (
                <option key={h} value={h}>
                  Till {hourLabel(h)}
                </option>
              ))}
            </select>
          )}
        </Box>
      </Box>

      {data.failed ? (
        <Box
          style={{
            border: `1px dashed ${LINE}`,
            borderRadius: 12,
            padding: "28px 16px",
            textAlign: "center",
            color: BODY,
            fontSize: 13,
          }}
        >
          Couldn't load the hourly numbers. Use the refresh button at the top of
          the page to try again.
        </Box>
      ) : (
        <>
          <Box
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 10,
            }}
          >
            {series.map((s, i) => {
              const v = sumTo(s.vals, cut);
              return (
                <Box
                  key={s.id}
                  style={{
                    border: `1px solid ${LINE}`,
                    borderLeft: `4px solid ${COLOR[s.id]}`,
                    borderRadius: 12,
                    padding: "11px 14px",
                  }}
                >
                  <Box style={{ fontSize: 12, color: MUTED, fontWeight: 600 }}>
                    {s.label} · {tillText}
                  </Box>
                  <Box
                    style={{
                      fontSize: 24,
                      fontWeight: 700,
                      letterSpacing: -0.5,
                      fontVariantNumeric: "tabular-nums",
                      color: INK,
                    }}
                  >
                    {loading ? "…" : show(v, s.avg)}
                  </Box>
                  <Box
                    style={{ fontSize: 12.5, fontWeight: 600, color: MUTED }}
                  >
                    {i === 0 ? (
                      isToday ? (
                        `as of ${asOfText}`
                      ) : (
                        dayLabel(baseDay)
                      )
                    ) : (
                      <>
                        {series[0].label} is{" "}
                        <Change
                          current={tidy(baseTotal, m)}
                          previous={tidy(v, { ...m, avg: s.avg })}
                        />
                      </>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {loading ? (
            <Box style={{ display: "grid", placeItems: "center", height: 220 }}>
              <CircularProgress size={26} style={{ color: G }} />
            </Box>
          ) : view === "table" ? (
            <HourTable series={series} cut={cut} show={show} money={m.money} />
          ) : (
            <HourChart
              series={series}
              cut={cut}
              isToday={isToday}
              now={now}
              show={show}
              money={m.money}
            />
          )}

          {!loading && <Highlights series={series} cut={cut} />}
        </>
      )}

      <Box
        style={{
          marginTop: 12,
          color: MUTED,
          fontSize: 11.5,
          display: "flex",
          gap: 14,
          flexWrap: "wrap",
        }}
      >
        {[
          "Abandoned is counted at the hour the lead came in.",
          "Paid and revenue are counted at the hour the payment completed.",
          "Revenue counts INR payments only.",
        ].map((t) => (
          <span
            key={t}
            style={{ display: "inline-flex", gap: 4, alignItems: "center" }}
          >
            <InfoOutlinedIcon style={{ fontSize: 13 }} />
            {t}
          </span>
        ))}
      </Box>
    </Box>
  );
};

const niceStep = (v) => {
  if (v <= 1) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  const f = v / p;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * p;
};

const HourChart = ({ series, cut, isToday, now, show, money }) => {
  const [hover, setHover] = useState(null);
  const boxRef = useRef(null);
  const W = 1000;
  const H = 290;
  const L = 52;
  const R = 10;
  const T = 12;
  const B = 34;
  const cw = W - L - R;
  const ch = H - T - B;
  const gw = cw / 24;
  const peak = Math.max(
    0,
    ...series.flatMap((s) => s.vals.filter((v) => v != null)),
  );
  const step = Math.max(1, niceStep(peak / 4));
  const ticks = Math.max(1, Math.ceil(peak / step));
  const max = step * ticks;
  const y = (v) => T + ch - (v / max) * ch;
  const k = series.length;
  const bw = Math.min(12, (gw * 0.74 - (k - 1) * 2) / k);
  const groupW = bw * k + (k - 1) * 2;
  const axisText = (v) =>
    money
      ? v >= 1000
        ? `₹${+(v / 1000).toFixed(1)}k`
        : `₹${v}`
      : v % 1
        ? v.toFixed(1)
        : v;

  return (
    <Box ref={boxRef} style={{ position: "relative", marginTop: 16 }}>
      <Box
        style={{
          display: "flex",
          gap: 14,
          flexWrap: "wrap",
          fontSize: 12,
          color: BODY,
          fontWeight: 600,
          marginBottom: 6,
        }}
      >
        {series.map((s) => (
          <span key={s.id}>
            <i
              style={{
                display: "inline-block",
                width: 10,
                height: 10,
                borderRadius: 3,
                marginRight: 6,
                background: COLOR[s.id],
                verticalAlign: -1,
              }}
            />
            {s.label}
          </span>
        ))}
        {isToday && (
          <span style={{ color: MUTED, fontWeight: 500 }}>
            faded = hours still to come
          </span>
        )}
      </Box>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{
          width: "100%",
          height: "auto",
          display: "block",
          overflow: "visible",
        }}
      >
        {Array.from({ length: ticks + 1 }, (_, i) => (
          <g key={i}>
            <line
              x1={L}
              x2={W - R}
              y1={y(step * i)}
              y2={y(step * i)}
              stroke="#eef2f0"
            />
            <text
              x={L - 8}
              y={y(step * i) + 4}
              textAnchor="end"
              fontSize="11"
              fill={MUTED}
            >
              {axisText(step * i)}
            </text>
          </g>
        ))}
        {Array.from({ length: 24 }, (_, h) => (
          <text
            key={h}
            x={L + gw * h + gw / 2}
            y={H - 12}
            textAnchor="middle"
            fontSize={h % 3 === 0 ? 11 : 9.5}
            fill={h % 3 === 0 ? "#55665e" : "#a9b6b0"}
          >
            {h % 3 === 0 ? hourLabel(h).replace(" ", "") : h % 12 || 12}
          </text>
        ))}
        {hover != null && (
          <rect
            x={L + gw * hover}
            y={T}
            width={gw}
            height={ch}
            rx={6}
            fill={INK}
            opacity={0.05}
          />
        )}
        {isToday && (
          <g>
            <line
              x1={L + gw * now.hour}
              x2={L + gw * now.hour}
              y1={T}
              y2={T + ch}
              stroke={INK}
              strokeDasharray="3 3"
              opacity={0.45}
            />
            <text
              x={L + gw * now.hour + 5}
              y={T + 10}
              fontSize="10.5"
              fontWeight="600"
              fill={BODY}
            >
              now{" "}
              {moment({ hour: now.hour, minute: now.minute }).format("h:mm A")}
            </text>
          </g>
        )}
        {Array.from({ length: 24 }, (_, h) => {
          const x0 = L + gw * h + (gw - groupW) / 2;
          return series.map((s, i) => {
            const v = s.vals[h];
            if (v == null || v <= 0) return null;
            const top = y(v);
            const hh = T + ch - top;
            const r = Math.min(3, bw / 2, hh);
            const x = x0 + i * (bw + 2);
            return (
              <path
                key={`${h}-${s.id}`}
                d={`M${x},${T + ch}V${top + r}q0,-${r} ${r},-${r}h${bw - 2 * r}q${r},0 ${r},${r}V${T + ch}Z`}
                fill={COLOR[s.id]}
                opacity={h >= cut ? (isToday ? 0.28 : 0.35) : 1}
              />
            );
          });
        })}
        <line x1={L} x2={W - R} y1={T + ch} y2={T + ch} stroke="#cdd8d2" />
        {peak === 0 && (
          <text
            x={L + cw / 2}
            y={T + ch / 2}
            textAnchor="middle"
            fontSize="13"
            fontWeight="600"
            fill={MUTED}
          >
            No activity in these hours
          </text>
        )}
        {Array.from({ length: 24 }, (_, h) => (
          <rect
            key={h}
            x={L + gw * h}
            y={T}
            width={gw}
            height={ch + B}
            fill="transparent"
            onMouseEnter={() => setHover(h)}
            onMouseLeave={() => setHover(null)}
          />
        ))}
      </svg>
      {hover != null && (
        <HourTip
          h={hover}
          series={series}
          show={show}
          left={(() => {
            const w = boxRef.current ? boxRef.current.offsetWidth : W;
            const s = w / W;
            const right = (L + gw * (hover + 1)) * s + 8;
            return right + 240 > w
              ? Math.max(0, (L + gw * hover) * s - 248)
              : right;
          })()}
        />
      )}
    </Box>
  );
};

const HourTip = ({ h, series, show, left }) => {
  const b = series[0].vals[h];
  return (
    <Box
      style={{
        position: "absolute",
        top: 40,
        left,
        pointerEvents: "none",
        background: "#fff",
        border: `1px solid ${LINE}`,
        borderRadius: 10,
        boxShadow: "0 8px 24px rgba(16,24,40,.14)",
        padding: "10px 12px",
        fontSize: 12,
        minWidth: 230,
        zIndex: 5,
      }}
    >
      <Box style={{ fontWeight: 700, marginBottom: 6, color: INK }}>
        {hourRange(h)}
      </Box>
      {series.map((s, i) => {
        const v = s.vals[h];
        const p = i && b != null ? pctChange(b, v) : null;
        return (
          <Box
            key={s.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "2px 0",
              color: BODY,
            }}
          >
            <i
              style={{
                width: 9,
                height: 9,
                borderRadius: 3,
                background: COLOR[s.id],
                flex: "none",
              }}
            />
            {s.short || s.label}
            <span
              style={{
                marginLeft: "auto",
                fontWeight: 700,
                color: INK,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {show(v, s.avg)}
            </span>
            <span
              style={{
                width: 56,
                textAlign: "right",
                fontWeight: 600,
                color: p == null ? MUTED : p >= 0 ? UP : DOWN,
              }}
            >
              {p == null ? "" : `${p > 0 ? "+" : ""}${p}%`}
            </span>
          </Box>
        );
      })}
      <Box style={{ color: MUTED, marginTop: 6, fontSize: 11 }}>
        {b == null
          ? "This hour hasn't finished yet."
          : `% = ${series[0].label} compared with each row`}
      </Box>
    </Box>
  );
};

const HourTable = ({ series, cut, show, money }) => {
  const cell = {
    textAlign: "right",
    padding: "6px 10px",
    borderBottom: "1px solid #f0f3f1",
    fontVariantNumeric: "tabular-nums",
  };
  const head = {
    ...cell,
    color: MUTED,
    fontWeight: 600,
    fontSize: 11.5,
    borderBottom: `1px solid ${LINE}`,
    position: "sticky",
    top: 0,
    background: "#fff",
  };
  const base = series[0];
  const rest = series.slice(1);
  return (
    <Box
      style={{
        marginTop: 16,
        maxHeight: 420,
        overflow: "auto",
        border: `1px solid ${LINE}`,
        borderRadius: 10,
      }}
    >
      <table
        style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}
      >
        <thead>
          <tr>
            <th style={{ ...head, textAlign: "left" }}>Hour (IST)</th>
            {series.map((s) => (
              <th key={s.id} style={head}>
                <i
                  style={{
                    display: "inline-block",
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: COLOR[s.id],
                    marginRight: 5,
                  }}
                />
                {s.short || s.label}
              </th>
            ))}
            {rest.map((s) => (
              <th key={s.id} style={head}>
                vs {s.short}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 24 }, (_, h) => {
            const b = base.vals[h];
            const faded = b == null || h >= cut;
            return (
              <tr key={h} style={{ color: faded ? "#b3beb8" : INK }}>
                <td style={{ ...cell, textAlign: "left" }}>{hourRange(h)}</td>
                {series.map((s) => (
                  <td key={s.id} style={cell}>
                    {show(s.vals[h], s.avg)}
                  </td>
                ))}
                {rest.map((s) => (
                  <td key={s.id} style={cell}>
                    {b == null ? (
                      "—"
                    ) : (
                      <Delta
                        current={tidy(b, { money })}
                        previous={tidy(s.vals[h], { money, avg: s.avg })}
                      />
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
          <tr style={{ fontWeight: 700, color: INK }}>
            <td
              style={{
                ...cell,
                textAlign: "left",
                borderTop: `1.5px solid ${LINE}`,
              }}
            >
              Total {cut === 24 ? "(full day)" : `till ${hourLabel(cut)}`}
            </td>
            {series.map((s) => (
              <td
                key={s.id}
                style={{ ...cell, borderTop: `1.5px solid ${LINE}` }}
              >
                {show(sumTo(s.vals, cut), s.avg)}
              </td>
            ))}
            {rest.map((s) => (
              <td
                key={s.id}
                style={{ ...cell, borderTop: `1.5px solid ${LINE}` }}
              >
                <Change
                  current={tidy(sumTo(base.vals, cut), { money })}
                  previous={tidy(sumTo(s.vals, cut), { money, avg: s.avg })}
                />
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </Box>
  );
};

/**
 * Biggest drop / best hour against the average (or previous day), plus the
 * usual peak. Hours below 15% of the reference's busiest hour are skipped:
 * 0 vs 0.4 is "-100%" and means nothing.
 */
const Highlights = ({ series, cut }) => {
  if (series.length < 2) return null;
  const ref = series.find((s) => s.id === "avg") || series[1];
  const b = series[0].vals;
  const floor = Math.max(2, Math.max(...ref.vals) * 0.15);
  let worst = null;
  let best = null;
  for (let h = 0; h < Math.min(cut, 24); h++) {
    if (b[h] == null || ref.vals[h] < floor) continue;
    const p = pctChange(b[h], ref.vals[h]);
    if (p == null) continue;
    if (!worst || p < worst.p) worst = { h, p };
    if (!best || p > best.p) best = { h, p };
  }
  const peak = ref.vals.reduce((pk, v, h) => (v > ref.vals[pk] ? h : pk), 0);
  const pill = {
    fontSize: 12,
    borderRadius: 999,
    padding: "5px 11px",
    background: "#f4f7f5",
    color: BODY,
  };
  return (
    <Box style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
      {worst && worst.p < -15 && (
        <span style={pill}>
          ⚠︎ Biggest drop: <b>{hourRange(worst.h)}</b>, {worst.p}% vs {ref.short}
        </span>
      )}
      {best && best.p > 15 && (
        <span style={pill}>
          ▲ Best hour: <b>{hourRange(best.h)}</b>, +{best.p}% vs {ref.short}
        </span>
      )}
      {ref.vals[peak] > 0 && (
        <Tooltip
          title={`The busiest hour on the ${ref.short} — useful for ad scheduling and sales follow-ups`}
        >
          <span style={pill}>
            ◷ Usual peak ({ref.short}): <b>{hourRange(peak)}</b>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};

export default HourlyComparison;
